// Some of this is directly copied from FP_ReceiveItemsModal.jsx for simplicity of implementation. This may be changed later to make it more effective.
// React and bootstrap
// React + Bootstrap
import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";

// DataTables core + Buttons (Bootstrap 5 styling)
import DataTable from "datatables.net-bs5";
import "datatables.net-buttons-bs5";
import "datatables.net-buttons/js/buttons.html5.js";
import "datatables.net-buttons/js/buttons.print.js";

// Export dependencies
import jszip from "jszip";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts"; // side-effect import; no pdfFonts variable

// Wire JSZip + pdfMake into DataTables Buttons
if (typeof window !== "undefined" && DataTable.Buttons) {
  DataTable.Buttons.jszip(jszip);
  DataTable.Buttons.pdfMake(pdfMake);
}



export function ExportTransactionsModal({
    show,
    onHide,
    selectedOrgID,
}) {
    const [fileType, setFileType] = useState("pdf");                 
    const [fileName, setFileName] = useState("Transaction_Report");  
    const [loading, setLoading] = useState(false);                   
    
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // helper to format JS Date -> "YYYY-MM-DD" string for <input type="date" />.
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Set default date range when modal opens:
    // From = today, To = tomorrow.
    useEffect(() => {
        if (show) {
            // When opening: default to today -> tomorrow
            const today = new Date();
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);

            const start = formatDate(today);
            const end = formatDate(tomorrow);

            setFileType("pdf");                     
            setFileName("Transaction_Report");      
            setLoading(false);                      
            setStartDate(start);                    
            setEndDate(end);                        
        } else {
            //when closing, you can clear or leave values; this keeps it "fresh" next open.
            setLoading(false);
        }
    }, [show]);

    

    // helper to generate the correct DataTables export button
    // based on the selected fileType (pdf/xlsx/print).
    const getExportButton = (fileType, fileName) => {
        switch (fileType) {
            case "pdf":
                return {
                    extend: "pdfHtml5",
                    title: fileName,
                    filename: fileName,
                    orientation: "landscape",
                    pageSize: "A4",
                    exportOptions: { columns: [1, 2, 4, 5, 6, 9] },
                    customize: function (doc) {
                    // Center title
                    if (doc.content[0]) {
                        doc.content[0].alignment = "center";
                    }

                    // Find the table node safely
                    const tableNode = doc.content.find(c => c.table);
                    if (tableNode) {
                        // Center the table as a whole
                        tableNode.alignment = "center";

                        // Center all cell contents
                        tableNode.table.body.forEach(row => {
                            row.forEach((cell, idx) => {
                                if (typeof cell === "string") {
                                row[idx] = { text: cell, alignment: "center" };
                                } else {
                                cell.alignment = "center";
                                }
                            });
                        });
                    }

                    // Margins
                    doc.pageMargins = [40, 40, 40, 40];

                    // Optional: center table headers via style
                    doc.styles.tableHeader.alignment = "center";
                    }
                };
            case "xlsx":
                return {
                    extend: "excelHtml5",
                    title: fileName,
                    filename: fileName,
                    exportOptions: { columns: [1,2,4,5,6,9] }
                };
            case "print":
                return {
                    extend: "print",
                    title: fileName,
                    exportOptions: { columns: [1,2,4,5,6,9] }
                };
            default:
                return null;
        }
    };

    
    const handleExport = async () => {
    if (!startDate || !endDate) {
        alert("Please select both a start and end date.");
        return;
    }

    if (new Date(startDate) > new Date(endDate)) {
        alert("Start date cannot be after end date.");
        return;
    }

    setLoading(true);

    try {
        const url = process.env.REACT_APP_BACKURL;
        // Get user's timezone offset in minutes
        const timezoneOffset = new Date().getTimezoneOffset();
        
        const res = await fetch(
            `${url}/api/foodPantry/reports/${selectedOrgID}/OrgTransactionReport?startDate=${startDate}&endDate=${endDate}&timezoneOffset=${-timezoneOffset}`,
            {
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                method: "GET"
            }
        );

        if (!res.ok) {
            throw new Error(`Server responded with status ${res.status}`);
        }

        const json = await res.json();
        const transactions = json.transactions || [];

        // Build the correct export button
        const exportButton = getExportButton(fileType, fileName);

        // Create a temporary, detached table element for DataTables export
        const tempTable = document.createElement("table");
        tempTable.id = "temp-transactions-export";
        
        // Add headers
        const thead = document.createElement("thead");
        const headerRow = document.createElement("tr");
        ["Inventory ID", "Transaction Date", "Type", "Mode", "Quantity", "Unit Value", "Line Value", "Item Variation ID", "Item ID", "Item Name"].forEach(header => {
            const th = document.createElement("th");
            th.textContent = header;
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        tempTable.appendChild(thead);
        
        // Add body
        const tbody = document.createElement("tbody");
        tempTable.appendChild(tbody);

        // Create DataTable on the temporary element
        const table = new DataTable(tempTable, {
            data: transactions,
            columns: [
                { data: "inventoryID", title: "Inv ID" },
        { data: "transactionDate", title: "Transaction Date" },
        { data: "transactionType", title: "Intake/Release" },
        { data: "transactionMode", title: "Mode" },
        { data: "quantity", title: "Qty" },
        { data: "unitValue", title: "Individual Price" },
        { data: "line_value", title: "Transaction Value" },
        { data: "itemVariationID", title: "Variation ID" },
        { data: "itemID", title: "Item ID" },
        { data: "item_name", title: "Item Name" }
            ],
            dom: "Bfrtip",
            buttons: exportButton ? [exportButton] : []
        });

        // Trigger export immediately
        if (exportButton) {
            table.button(0).trigger();
        }

        // Clean up after a delay to allow download
        setTimeout(() => {
            table.destroy();
            tempTable.remove();
        }, 1000);

        } catch (err) {
            console.error("Export failed:", err);
            alert("There was an error generating the export. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            aria-labelledby="export-transactions-modal"
            centered
        >
            <Modal.Header>
                <Modal.Title id="export-transactions-modal">Export Transactions</Modal.Title>
                <Button variant="light" onClick={onHide}>
                    <i className="fa fa-times" />
                </Button>
            </Modal.Header>

            <Modal.Body>
                <Form>
                    {/* Same structure as before for file type, but fileType now drives the export logic */}
                    <Form.Group className="mb-3" controlId="exportFileType">
                        <Form.Label>File Type</Form.Label>
                        <Form.Select
                            value={fileType}
                            onChange={(e) => setFileType(e.target.value)} //   now meaningfully used in handleExport
                        >
                            <option value="pdf">PDF</option>
                            <option value="xlsx">Excel</option>
                            <option value="print">Print</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Select what file type you would like to export to or if you would like to print the report.
                        </Form.Text>
                    </Form.Group>

                    {/*   Replaced the timeframe dropdown with a start/end date range input pair */}
                    <Form.Group className="mb-3" controlId="exportDateRange">
                        <Form.Label>Date Range</Form.Label>
                        <div className="d-flex align-items-center" style={{ gap: "31px", paddingBottom: "10px"}}>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    var tStart = new Date(endDate);
                                    tStart.setMonth(tStart.getMonth() - 3);
                                    setStartDate(formatDate(tStart));
                                    console.log("here");
                                }}
                            >
                                3 Months
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    var tStart = new Date(endDate);
                                    tStart.setMonth(tStart.getMonth() - 6);
                                    setStartDate(formatDate(tStart));
                                }}
                            >
                                6 Months
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    var tStart = new Date(endDate);
                                    tStart.setMonth(tStart.getMonth() - 12);
                                    setStartDate(formatDate(tStart));
                                }}
                            >
                                12 Months
                            </Button>
                        </div>

                        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                            <div
                                className="date-range-container no-print d-flex align-items-center flex-wrap"
                                style={{ gap: "10px" }}
                            >
                                <span className="date-range-label">From</span>
                                <input
                                    type="date"
                                    className="date-range-input form-control"
                                    value={startDate}                     //   bound to startDate state
                                    onChange={(e) => setStartDate(e.target.value)} //   updates startDate state
                                />

                                <span className="date-range-label">To</span>
                                <input
                                    type="date"
                                    className="date-range-input form-control"
                                    value={endDate}                       //   bound to endDate state
                                    onChange={(e) => setEndDate(e.target.value)}   //   updates endDate state
                                />
                            </div>
                        </div>

                        <Form.Text className="text-muted">
                            Select a custom start and end date for the export.
                        </Form.Text>
                    </Form.Group>

                    {/* Same as before, but fileName is now used as the export title/filename. */}
                    <Form.Group className="mb-3" controlId="exportFileName">
                        <Form.Label>File Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)} //   used in DataTables filename/title
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                {/*   Button text now reflects loading state controlled by handleExport */}
                <Button variant="primary" onClick={handleExport} disabled={loading}>
                    {loading ? "Preparing..." : "Export"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}